import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiPost } from '../../utils/api';
import './IdentityChangeDialog.css';

// 직원 id 가 있는 행의 성명·주민번호를 고칠 때 띄우는 창.
// 그 직원이 어느 달 어느 시트에 어떤 값으로 들어 있는지 모두 보여 주고, 이 시트만 바꿀지 전체를 바꿀지 고른다.
// 바꾸려는 주민번호를 이 기업의 다른 직원이 쓰고 있으면 적용하지 않고 병합을 안내한다(자동으로 합치지 않는다).

const API = '/api/payroll';
const KEY_LABELS = { name: '성명', id_number: '주민번호' };
const SHEET_SHORT = { fulltime: '상', daily: '일', business: '사' };
// 값마다 다른 색. 바뀐 뒤의 값은 늘 첫 번째 색이다
const TONES = ['new', 'a', 'b', 'c', 'd', 'e'];
const pad = (n) => String(n).padStart(2, '0');
const ym = (p) => `${p.year}-${pad(p.month)}`;
const show = (v) => (v === null || v === undefined || v === '' ? '(빈칸)' : v);

// 그 직원이 들어 있는 곳을 연도 × 1~12월 표로. 한 칸에 시트마다 조각이 하나씩 들어간다.
function PlacesTable({ places, valueOf, toneOf, targets, sheetLabels, currentRowId, onGo }) {
  const years = [...new Set(places.map((p) => p.year))].sort((a, b) => b - a);
  return (
    <table className="pr-id-places">
      <thead>
        <tr><th />{Array.from({ length: 12 }, (_, i) => <th key={i}>{i + 1}월</th>)}</tr>
      </thead>
      <tbody>
        {years.map((y) => (
          <tr key={y}>
            <th>{y}년</th>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const here = places.filter((p) => p.year === y && p.month === m);
              return (
                <td key={m}>
                  {here.map((p) => {
                    const value = valueOf(p);
                    const changing = targets.has(p.row_id);
                    return (
                      <button
                        type="button"
                        key={p.row_id}
                        className={`pr-id-chip tone-${toneOf(value)} ${changing ? 'changing' : ''} ${p.row_id === currentRowId ? 'current' : ''}`}
                        title={`${y}년 ${m}월 · ${sheetLabels[p.sheet] || p.sheet} ${p.order}번째 줄\n${show(p.name)} · ${show(p.id_number)}${changing ? '\n이번에 바뀝니다' : ''}\n누르면 이 줄로 이동합니다(변경은 적용하지 않습니다)`}
                        onClick={() => onGo(p)}
                      >
                        {SHEET_SHORT[p.sheet] || p.sheet}
                      </button>
                    );
                  })}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IdentityChangeDialog({ companyId, change, remaining = 0, onDone, onGo }) {
  const keys = useMemo(() => Object.keys(KEY_LABELS).filter((k) => change[k]), [change]);
  const request = useMemo(() => {
    const body = { row_id: change.row_id };
    keys.forEach((k) => { body[k] = change[k].after; });
    return body;
  }, [change, keys]);
  // 주민번호를 고치는 것은 대개 정정이라 전체, 성명만 고치는 것은 대개 개명이라 이 시트만을 먼저 고른다
  const [scope, setScope] = useState(change.id_number ? 'all' : 'sheet');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mergeArmed, setMergeArmed] = useState(false);
  const url = `${API}/companies/${companyId}/employees/${change.employee_id}/identity`;

  const load = useCallback(async () => {
    setError('');
    try {
      setPreview(await apiPost(`${url}/preview`, request));
    } catch (e) {
      setError(e.message);
    }
  }, [url, request]);
  useEffect(() => { load(); }, [load]);

  const valueOf = useCallback((p) => keys.map((k) => show(p[k])).join(' · '), [keys]);

  const model = useMemo(() => {
    if (!preview) return null;
    const after = keys.map((k) => show(preview.new[k])).join(' · ');
    const counts = new Map([[after, 0]]);
    preview.places.forEach((p) => counts.set(valueOf(p), (counts.get(valueOf(p)) || 0) + 1));
    const values = [...counts.keys()];
    const toneOf = (value) => TONES[Math.min(values.indexOf(value), TONES.length - 1)];
    const targets = new Set(preview.targets[scope]);
    const months = new Set(preview.places.map(ym));
    const here = ym(preview.row);
    // 이 시트만 바꾸면 옛 값으로 남는 이후 달
    const laterOld = new Set(preview.places.filter((p) => ym(p) > here && valueOf(p) !== after).map(ym));
    return { after, counts, values, toneOf, targets, months, laterOld };
  }, [preview, scope, keys, valueOf]);

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      onDone(await apiPost(url, { ...request, scope }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const merge = async () => {
    setBusy(true);
    setError('');
    try {
      await apiPost(`${API}/companies/${companyId}/employees/merge`, {
        keep_employee_id: change.employee_id, merge_employee_id: preview.conflict.employee_id,
      });
      setMergeArmed(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const conflict = preview && preview.conflict;
  const labels = (preview && preview.sheet_labels) || {};

  return (
    <div className="pr-id-backdrop">
      <div className="pr-id" role="dialog" aria-label="성명·주민번호 변경">
        <div className="pr-id-head">
          <b>{keys.map((k) => KEY_LABELS[k]).join('·')} 변경</b>
          {remaining > 0 && <span className="pr-id-more">이 뒤에 {remaining}건 더 있습니다</span>}
        </div>

        <table className="pr-id-diff">
          <tbody>
            {keys.map((k) => (
              <tr key={k}>
                <th>{KEY_LABELS[k]}</th>
                <td className="before">{show(change[k].before)}</td>
                <td className="arrow">→</td>
                <td className="after">{show(preview ? preview.new[k] : change[k].after)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {error && <div className="pr-id-error">{error}</div>}
        {!preview && !error && <div className="pr-id-loading">이 직원이 들어 있는 곳을 찾는 중…</div>}

        {preview && model && (
          <>
            {!conflict && (
              <div className="pr-id-scopes">
                <label className={scope === 'sheet' ? 'on' : ''}>
                  <input type="radio" name="pr-id-scope" checked={scope === 'sheet'} onChange={() => setScope('sheet')} />
                  <span><b>이 시트만</b> {preview.row.year}년 {preview.row.month}월 {labels[preview.row.sheet]}의 이 줄만 바꿉니다. 과거 달은 그대로 두는 개명에 씁니다.</span>
                </label>
                <label className={scope === 'all' ? 'on' : ''}>
                  <input type="radio" name="pr-id-scope" checked={scope === 'all'} onChange={() => setScope('all')} />
                  <span><b>전체</b> 이 직원이 들어 있는 모든 달을 바꿉니다. 오타나 주민번호 정정에 씁니다.</span>
                </label>
              </div>
            )}

            <div className="pr-id-section">
              이 직원이 들어 있는 곳 <span className="pr-id-muted">{model.months.size}개 달 · {preview.places.length}개 줄 · 칸을 누르면 그 줄로 이동합니다</span>
            </div>
            <PlacesTable
              places={preview.places}
              valueOf={valueOf}
              toneOf={model.toneOf}
              targets={conflict ? new Set() : model.targets}
              sheetLabels={labels}
              currentRowId={preview.row.row_id}
              onGo={onGo}
            />
            <div className="pr-id-legend">
              {model.values.map((value) => (
                <span key={value} className="pr-id-legend-item">
                  <i className={`pr-id-chip tone-${model.toneOf(value)}`} />
                  {value}
                  <span className="pr-id-muted">{value === model.after && !model.counts.get(value) ? '바뀐 뒤의 값' : `${model.counts.get(value)}개 줄`}</span>
                </span>
              ))}
              <span className="pr-id-legend-item pr-id-muted">상 상용직 · 일 일용직 · 사 사업소득 · 굵은 테두리 = 이번에 바뀜</span>
            </div>

            {!conflict && (
              <div className="pr-id-summary">
                {model.targets.size
                  ? `${preview.places.length}개 줄 중 ${model.targets.size}개가 바뀝니다.`
                  : '바뀌는 줄이 없습니다(이미 같은 값입니다).'}
                {scope === 'sheet' && model.laterOld.size > 0 && (
                  <span className="warn"> 이미 만들어 둔 이후 달 {model.laterOld.size}개({[...model.laterOld].sort().join(', ')})는 옛 값으로 남습니다.</span>
                )}
              </div>
            )}

            {conflict && (
              <div className="pr-id-conflict">
                <p>
                  <b>이 기업의 다른 직원이 이 주민번호를 쓰고 있어 적용할 수 없습니다.</b><br />
                  {show(conflict.name)} · {show(conflict.id_number)}
                  {conflict.more > 0 && ` 외 ${conflict.more}명`}
                </p>
                <div className="pr-id-section">그 직원이 들어 있는 곳 <span className="pr-id-muted">{conflict.places.length}개 줄</span></div>
                <PlacesTable
                  places={conflict.places}
                  valueOf={valueOf}
                  toneOf={() => 'other'}
                  targets={new Set()}
                  sheetLabels={labels}
                  currentRowId={null}
                  onGo={onGo}
                />
                {conflict.overlaps.length > 0 ? (
                  <p className="warn">
                    두 직원이 같은 달 같은 시트에 함께 있습니다({conflict.overlaps.map((o) => `${ym(o)} ${labels[o.sheet] || o.sheet}`).join(', ')}).
                    서로 다른 사람일 가능성이 높습니다. 주민번호를 다시 확인해 주세요.
                  </p>
                ) : (
                  <p>두 직원이 같은 달 같은 시트에 함께 있는 곳은 없습니다. 같은 사람이 둘로 나뉘어 등록된 것이라면 병합하세요.</p>
                )}
                <div className="pr-id-merge">
                  {mergeArmed ? (
                    <>
                      <span>병합하면 그 직원의 모든 달 줄과 메모가 이 직원으로 옮겨집니다. 줄에 적힌 성명·주민번호는 바뀌지 않습니다.</span>
                      <button type="button" className="pr-btn danger" disabled={busy} onClick={merge}>병합 확정</button>
                      <button type="button" className="pr-btn" disabled={busy} onClick={() => setMergeArmed(false)}>아니오</button>
                    </>
                  ) : (
                    <button type="button" className="pr-btn" disabled={busy} onClick={() => setMergeArmed(true)}>같은 사람입니다 — 병합</button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="pr-id-actions">
          <button type="button" className="pr-btn" disabled={busy} onClick={() => onDone(null)}>바꾸지 않음</button>
          <button type="button" className="pr-btn primary" disabled={busy || !preview || Boolean(conflict) || !model || !model.targets.size} onClick={apply}>
            {scope === 'all' ? '전체에 적용' : '이 시트만 적용'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IdentityChangeDialog;
